import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { graphql } from 'gatsby';

import Header from '../components/Header';
import TopMenu from '../components/TopMenu';
import Footer from '../components/Footer';
import Resource from '../components/Resource';
import HighlightBox from '../components/HighlightBox';
import ExternalLink from '../components/ExternalLink';
import styles from './index.module.scss';

import { menuNodesSorter, listOrderSorter } from '../utils';

export default class HomePage extends PureComponent {
  constructor() {
    super();

    this.state = {
      indexEdges: [],
    };
  }

  componentDidMount() {
    const { data: { allMarkdownRemark: { edges } } } = this.props;

    this.setState({
      indexEdges: edges.filter(e => e.node.frontmatter.path.startsWith('/index')),
    });
  }

  renderContent = () => {
    const { indexEdges } = this.state;
    const introTextNode = indexEdges.find(({ node: { frontmatter: { section } } }) => section === 'introText');
    const getToKnowNode = indexEdges.find(({ node: { frontmatter: { section } } }) => section === 'getToKnow');
    const publications = indexEdges
      .filter(({ node: { frontmatter: { type } } }) => type === 'publications')
      .sort(listOrderSorter)
      .map(({ node: { frontmatter: { imageUrl: imgSrc, description: text, link, title } } }) => ({
        imgSrc,
        text,
        link,
        title,
      }));
    const highlights = [
      {
        imgSrc: 'https://firebasestorage.googleapis.com/v0/b/fao-aquastat.appspot.com/o/Images%2Fhomepage%2Faquastat-database.jpg?alt=media&token=e3200975-3291-43a6-92ad-eebfa4075296',
        link: 'http://fao.org/aquastat/statistics/query/index.html?lang=en',
        label: 'Statistiques par Pays',
      },
      {
        imgSrc: 'https://firebasestorage.googleapis.com/v0/b/fao-aquastat.appspot.com/o/Images%2Fhomepage%2Fmaps.jpg?alt=media&token=38e27523-ece0-4ab5-a5b0-a438b258a2a5',
        link: 'http://data.apps.fao.org/aquamaps/',
        label: 'Cartes',
      },
      {
        imgSrc: 'https://firebasestorage.googleapis.com/v0/b/fao-aquastat.appspot.com/o/Images%2Fhomepage%2Fcountry-profile.jpg?alt=media&token=dfcaf504-4d77-4a60-ba47-05a37be384ed',
        link: '/countries-and-basins/country-profiles/',
        label: 'Profils de Pays',
      },
    ];

    return (
      <div className={styles.content}>
        <div className={styles.introText} dangerouslySetInnerHTML={{ __html: introTextNode && introTextNode.node.html }} />

        <div className={styles.contentSection}>
          <h3>Points forts</h3>

          <div className={styles.highlights}>
            {highlights.map(props => <HighlightBox key={props.label} {...props} />)}
          </div>
        </div>

        <div className={styles.contentSection}>
          <h3>Lumière sur</h3>

          <div className={styles.getToKnowText}>
            <img
              src="https://firebasestorage.googleapis.com/v0/b/fao-aquastat.appspot.com/o/Images%2Fhomepage%2Fgettoknow_3.jpg?alt=media&token=3e05dbdd-5c2a-4a64-a50a-f9a75f3c7bd1"
              alt="Lumière sur"
            />

            <span dangerouslySetInnerHTML={{ __html: getToKnowNode && getToKnowNode.node.html }} />
          </div>
        </div>

        <div className={styles.contentSection}>
          <h3>Principales Publications</h3>
          {publications.map(props => <Resource key={props.title} {...props} />)}
        </div>
      </div>
    );
  };

  renderSidebarLinks = () => {
    const { indexEdges } = this.state;
    const links = indexEdges
      .filter(({ node: { frontmatter: { type } } }) => type === 'links')
      .sort(menuNodesSorter);
    const projectsLinks = links
      .filter(({ node: { frontmatter: { section } } }) => section === 'projects')
      .sort(menuNodesSorter);
    const seeAlsoLinks = links
      .filter(({ node: { frontmatter: { section } } }) => section === 'seeAlso')
      .sort(menuNodesSorter);
    const externalLinks = links
      .filter(({ node: { frontmatter: { section } } }) => section === 'externalLinks')
      .sort(menuNodesSorter);

    return [
      <div key="Projects" className={styles.sidebarBox}>
        <h3>Projets</h3>
        {projectsLinks.map(({ node: { frontmatter: { link, title } } }) => (
          <ExternalLink key={link} link={link} label={title} icon="world" />
        ))}
      </div>,
      <div key="See also" className={styles.sidebarBox}>
        <h3>Voir aussi</h3>
        {seeAlsoLinks.map(({ node: { frontmatter: { link, title } } }) => (
          <ExternalLink key={link} link={link} label={title} icon="world" />
        ))}
      </div>,
      <div key="External links" className={styles.sidebarBox}>
        <h3>Liens connexes</h3>
        {externalLinks.map(({ node: { frontmatter: { link, title } } }) => (
          <ExternalLink key={link} link={link} label={title} icon="world" />
        ))}
      </div>,
    ];
  }

  renderSidebar = () => {
    const { indexEdges } = this.state;
    const didYouKnowNode = indexEdges.find(({ node: { frontmatter: { section } } }) => section === 'didYouKnow');
    const recentlyUpdatedNode = indexEdges.find(({ node: { frontmatter: { section } } }) => section === 'recentlyUpdated');

    return (
      <div className={styles.sidebar}>
        <div className={styles.didYouKnowBox}>
          <h3>Le saviez-vous?</h3>
          <div dangerouslySetInnerHTML={{ __html: didYouKnowNode && didYouKnowNode.node.html }} />
        </div>

        {(recentlyUpdatedNode && recentlyUpdatedNode.node.html !== '') && (
          <div className={styles.sidebarBox}>
            <h3>Recently updated</h3>
            <div dangerouslySetInnerHTML={{ __html: recentlyUpdatedNode.node.html }} />
          </div>
        )}

        {this.renderSidebarLinks()}

        <a className="twitter-timeline"  data-height="400" data-lang="fr" href="https://twitter.com/FAOLandWater?ref_src=twsrc%5Etfw">
          <h3>Tweets de</h3> @FAOLandWater
        </a>

        <div className={styles.contactBox}>
          <h3>Contactez-nous</h3>
          <span>
          	Pour plus d'informations, contacter: <a href="mailto:aquastat@fao.org">aquastat@fao.org</a>
          </span>
        </div>
      </div>
    );
  };

  render() {
    const { data: { site }, location } = this.props;

    return (
      <div>
        <Helmet
          title={site.siteMetadata.title}
          meta={[
            { name: 'description', content: 'FAO AQUASTAT' },
            { name: 'keywords', content: 'fao, water, aquastat, water production, eau' },
          ]}
        />
        <Header siteTitle={site.siteMetadata.title} location={{}} />

        <div className="app-wrapper">
          <h2 className="site-title">AQUASTAT - Système d'information mondial de la FAO sur l'eau et l'agriculture</h2>
          <TopMenu location={location.pathname} />

          <div className={styles.wrapper}>
            <div className={styles.headerImage}>
              <img src="https://firebasestorage.googleapis.com/v0/b/fao-aquastat.appspot.com/o/Images%2Fbanners%2Fhome1.jpg?alt=media&token=4daae394-9fb6-416f-8ab3-7e4e029ed9a8" alt="FAO AQUASTAT" />
            </div>
            {this.renderContent()}
            {this.renderSidebar()}
          </div>
        </div>

        <Footer />
      </div>
    );
  }
}

HomePage.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

/* eslint-disable no-undef */
export const pageQuery = graphql`
  query HomePageQuery {
    site {
      siteMetadata {
        title
      }
    }
    allMarkdownRemark {
      edges {
        node {
          id
          html
          frontmatter {
            path
            title
            section
            type
            link
            imageUrl
            menuOrder
            listOrder
            description
          }
        }
      }
    }
  }
`;
